<script setup>
import SendEmailingAutomationTestEmailAction from '@frontJs/components/actions/projectManagement/modules/SendEmailingAutomationTestEmailAction.vue';
import EnvelopeIcon from '@iconify-vue/ph/envelope';
import FileTextIcon from '@iconify-vue/ph/file-text';
import NumberOneIcon from '@iconify-vue/ph/number-one';
import NumberTwoIcon from '@iconify-vue/ph/number-two';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import DisplayHtmlPreviewAction from '@js/components/actions/DisplayHtmlPreviewAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

function buildTemplatePreviews(row) {
    const previews = [];

    if (row.htmlPreview) {
        previews.push(
            h(
                DisplayHtmlPreviewAction,
                { title: 'Email initial', html: row.htmlPreview },
                {
                    trigger: () =>
                        h(ActionButton, {
                            size: 'small',
                            square: true,
                            title: "Voir l'email initial",
                            type: 'primary',
                            icon: FileTextIcon,
                        }),
                },
            ),
        );
    }

    (row.stepPreviews ?? []).forEach((step) => {
        previews.push(
            h(
                DisplayHtmlPreviewAction,
                {
                    title: `Relance ${step.position - 1}`,
                    html: step.htmlPreview,
                },
                {
                    trigger: () =>
                        h(ActionButton, {
                            size: 'small',
                            title: `Voir la relance ${step.position - 1}`,
                            type: 'secondary',
                            square: true,
                            icon: [NumberOneIcon, NumberTwoIcon][
                                step.position - 2
                            ],
                        }),
                },
            ),
        );
    });

    return previews;
}

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.type.label,
                }),
        }),
        columnHelper.display({
            id: 'template',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Template',
                }),
            cell: (props) => {
                const templates = buildTemplatePreviews(props.row.original);

                if (templates.length === 0) {
                    return null;
                }

                return h(
                    BaseCell,
                    {},
                    {
                        default: () => templates,
                    },
                );
            },
        }),
        columnHelper.accessor(() => null, {
            id: 'isActive',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Est actif',
                }),
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isActive,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'isBeingTested',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Est en test',
                }),
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isBeingTested,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'isInvoiced',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Est facturé',
                }),
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isInvoiced,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfEmailsCanBeSend',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'NB emails<br/>Pouvant être envoyés',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.numberOfEmailsCanBeSend,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfEmailsSent',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'NB emails<br/>envoyés',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.numberOfEmailsSent,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfEmailsPending',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'NB emails<br/>en attente',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.numberOfEmailsPending,
                }),
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.modules.emailingAutomation',
                                    {
                                        tenant: tenant.id,
                                        emailingAutomation:
                                            props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                square: true,
                            }),
                            h(
                                SendEmailingAutomationTestEmailAction,
                                {
                                    tenant,
                                    emailingAutomation: props.row.original,
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            title: 'Envoyer un email de test',
                                            type: 'secondary',
                                            square: true,
                                            icon: EnvelopeIcon,
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.modules.postEmailingAutomations', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route(
                        'front.projectManagement.modules.emailingAutomation',
                        {
                            tenant: tenant.id,
                        },
                    )
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
